import React, { useState } from 'react';
import * as MuiCore from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import CSVUploader from './CSVUploader';

const filterRegexPatterns = [
    { pattern: /["""]/g, replaceWith: "'" },
    { pattern: /[{}()[\]:;$€£¥₹¢₽₩₺₴₦₱]/g, replaceWith: '' },
];

const filterByRegexPatterns = (text, patterns) => {
    let result = text;
    patterns.forEach(({ pattern, replaceWith }) => {
        result = result.replace(pattern, replaceWith);
    });
    return result;
};

const FaqCard = ({
    faqItems,
    editing,
    tempItems,
    onEdit,
    onSave,
    onCancel,
    onAddItem,
    onRemoveItem,
    onQuestionChange,
    onAnswerChange,
    onCsvUpload,
    errors,
    classes
}) => {
    const [showCsvDialog, setShowCsvDialog] = useState(false);
    const [csvSubmitDisabled, setCsvSubmitDisabled] = useState(true);
    const [pendingCsvItems, setPendingCsvItems] = useState(null);

    const maxItems = 50;
    const questionMaxLength = 200;
    const answerMaxLength = 500;

    const handleCsvFileChange = (data) => {
        if (data) {
            const items = data.rows.map(row => ({
                question: filterByRegexPatterns(row[0], filterRegexPatterns),
                answer: filterByRegexPatterns(row[1], filterRegexPatterns),
            }));
            setPendingCsvItems(items);
            setCsvSubmitDisabled(false);
        } else {
            setPendingCsvItems(null);
            setCsvSubmitDisabled(true);
        }
    };

    const handleCsvConfirm = () => {
        if (pendingCsvItems && onCsvUpload) {
            onCsvUpload(pendingCsvItems);
        }
        setShowCsvDialog(false);
        setPendingCsvItems(null);
        setCsvSubmitDisabled(true);
    };

    const handleCsvCancel = () => {
        setShowCsvDialog(false);
        setPendingCsvItems(null);
        setCsvSubmitDisabled(true);
    };

    return (
        <MuiCore.Box className={classes.section}>
            <MuiCore.Box className={classes.sectionHeader}>
                <MuiCore.Box className={classes.sectionTitle}>
                    <MuiCore.SvgIcon>
                        <path d="M11 18h2v-2h-2v2zm1-16C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-14c-2.21 0-4 1.79-4 4h2c0-1.1.9-2 2-2s2 .9 2 2c0 2-3 1.75-3 5h2c0-2.25 3-2.5 3-5 0-2.21-1.79-4-4-4z"/>
                    </MuiCore.SvgIcon>
                    <MuiCore.Typography variant="h6" style={{ fontFamily: 'Sofia-bold' }}>FAQ</MuiCore.Typography>
                </MuiCore.Box>
                {!editing && (
                    <MuiCore.Button
                        startIcon={<EditIcon />}
                        className={classes.editButton}
                        variant="outlined"
                        onClick={onEdit}
                    >
                        EDIT
                    </MuiCore.Button>
                )}
            </MuiCore.Box>
            <MuiCore.Typography variant="body2" color="textSecondary" style={{ marginBottom: 16, fontFamily: 'Sofia' }}>
                Your Grasshopper AI will use these FAQs to answer your callers' questions
            </MuiCore.Typography>
            {editing ? (
                <>
                    {tempItems.map((item, index) => (
                        <MuiCore.Box
                            key={index}
                            style={{
                                marginBottom: 24,
                                padding: 16,
                                border: '1px solid #e0e0e0',
                                borderRadius: 4
                            }}
                        >
                            <MuiCore.Box
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: 12
                                }}
                            >
                                <MuiCore.Typography
                                    variant="subtitle1"
                                    style={{ fontWeight: 'bold', color: '#7F2062', fontFamily: 'Sofia' }}
                                >
                                    FAQ {index + 1}
                                </MuiCore.Typography>
                                <MuiCore.IconButton
                                    size="small"
                                    onClick={() => onRemoveItem(index)}
                                    style={{ color: '#d32f2f' }}
                                >
                                    <CloseIcon />
                                </MuiCore.IconButton>
                            </MuiCore.Box>
                            <MuiCore.TextField
                                label="Question"
                                fullWidth
                                value={item.question}
                                onChange={(e) => onQuestionChange(index, e.target.value)}
                                variant="outlined"
                                size="small"
                                style={{ marginBottom: 12 }}
                                placeholder="Enter your question"
                                inputProps={{ maxLength: 200, style: { fontFamily: 'Sofia' } }}
                                InputLabelProps={{ style: { fontFamily: 'Sofia' } }}
                                InputProps={{ style: { fontFamily: 'Sofia' } }}
                                error={!!errors[`question_${index}`]}
                                helperText={errors[`question_${index}`] || ''}
                            />
                            <MuiCore.Typography
                                variant="caption"
                                style={{
                                    display: 'block',
                                    textAlign: 'right',
                                    marginTop: 4,
                                    marginBottom: 12,
                                    color: item.question.length > 200 ? '#d32f2f' : '#666',
                                    fontFamily: 'Sofia'
                                }}
                            >
                                {item.question.length}/200 Characters
                            </MuiCore.Typography>
                            <MuiCore.TextField
                                label="Answer"
                                fullWidth
                                multiline
                                rows={3}
                                value={item.answer}
                                onChange={(e) => onAnswerChange(index, e.target.value)}
                                variant="outlined"
                                size="small"
                                placeholder="Enter your answer"
                                inputProps={{ maxLength: 500, style: { fontFamily: 'Sofia' } }}
                                InputLabelProps={{ style: { fontFamily: 'Sofia' } }}
                                InputProps={{ style: { fontFamily: 'Sofia' } }}
                                error={!!errors[`answer_${index}`]}
                                helperText={errors[`answer_${index}`] || ''}
                            />
                            <MuiCore.Typography
                                variant="caption"
                                style={{
                                    display: 'block',
                                    textAlign: 'right',
                                    marginTop: 4,
                                    color: item.answer.length > 500 ? '#d32f2f' : '#666',
                                    fontFamily: 'Sofia'
                                }}
                            >
                                {item.answer.length}/500 Characters
                            </MuiCore.Typography>
                        </MuiCore.Box>
                    ))}
                    <MuiCore.Button
                        startIcon={
                            <MuiCore.SvgIcon>
                                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                            </MuiCore.SvgIcon>
                        }
                        variant="outlined"
                        onClick={onAddItem}
                        style={{
                            marginBottom: 8,
                            borderColor: '#7F2062',
                            color: '#7F2062',
                            fontFamily: 'Sofia',
                            textTransform: 'none',
                            borderRadius: 100
                        }}
                    >
                        Add FAQ Item
                    </MuiCore.Button>
                    <MuiCore.Box style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                        <MuiCore.Button
                            variant="outlined"
                            onClick={() => {
                                setShowCsvDialog(true);
                                setCsvSubmitDisabled(true);
                                setPendingCsvItems(null);
                            }}
                            style={{
                                borderColor: '#7F2062',
                                color: '#7F2062',
                                fontFamily: 'Sofia',
                                textTransform: 'none',
                                borderRadius: 100,
                            }}
                        >
                            Upload CSV file
                        </MuiCore.Button>
                        <MuiCore.Tooltip title="You can upload a csv file containing your FAQs. Please note doing so will replace all the FAQs you have entered below">
                            <MuiCore.IconButton
                                size="small"
                                style={{ marginLeft: 4, color: 'rgba(0,0,0,0.54)' }}
                            >
                                <InfoOutlinedIcon fontSize="small" />
                            </MuiCore.IconButton>
                        </MuiCore.Tooltip>
                    </MuiCore.Box>
                    <MuiCore.Box className={classes.actionButtons}>
                        <MuiCore.Button
                            startIcon={<CloseIcon />}
                            onClick={onCancel}
                            variant="outlined"
                            style={{ fontFamily: 'Sofia', textTransform: 'none', borderRadius: 100 }}
                        >
                            Cancel
                        </MuiCore.Button>
                        <MuiCore.Button
                            startIcon={<CheckIcon />}
                            onClick={onSave}
                            variant="contained"
                            style={{ backgroundColor: '#7F2062', color: '#fff', fontFamily: 'Sofia', fontWeight: 700, textTransform: 'none', borderRadius: 100 }}
                        >
                            Save
                        </MuiCore.Button>
                    </MuiCore.Box>
                </>
            ) : (
                <MuiCore.Box>
                    {faqItems.length > 0 ? (
                        faqItems.map((item, index) => (
                            <MuiCore.Box key={index} style={{ marginBottom: 16 }}>
                                <MuiCore.Typography style={{ fontWeight: 'bold', marginBottom: 4, fontFamily: 'Sofia' }}>
                                    Q: {item.question}
                                </MuiCore.Typography>
                                <MuiCore.Typography style={{ paddingLeft: 16, whiteSpace: 'pre-wrap', fontFamily: 'Sofia' }}>
                                    A: {item.answer}
                                </MuiCore.Typography>
                            </MuiCore.Box>
                        ))
                    ) : (
                        <MuiCore.Typography color="textSecondary" style={{ fontFamily: 'Sofia' }}>No FAQ content</MuiCore.Typography>
                    )}
                </MuiCore.Box>
            )}

            {/* CSV Upload Dialog */}
            <MuiCore.Dialog
                open={showCsvDialog}
                onClose={(event, reason) => { if (reason !== 'backdropClick') handleCsvCancel(); }}
                maxWidth="sm"
                fullWidth
            >
                <MuiCore.DialogTitle style={{ fontFamily: 'Sofia', borderBottom: '1px solid #e0e0e0' }}>
                    Upload CSV File
                </MuiCore.DialogTitle>
                <MuiCore.DialogContent style={{ paddingTop: 24 }}>
                    <CSVUploader
                        showConfirmButton={false}
                        validateParsedData={(data) => {
                            if (data.headers.length !== 2) {
                                return { error: 'Please provide a csv with two columns. One for question and another for answer.' };
                            }
                            if (data.totalRows > maxItems) {
                                return { error: `Cannot enter more than ${maxItems} FAQs.` };
                            }
                            for (let i = 0; i < data.totalRows; i++) {
                                const row = data.rows[i];
                                if (row[0].length === 0) {
                                    return { error: `Row ${i + 2} Question is empty` };
                                }
                                if (row[0].length > questionMaxLength) {
                                    return { error: `Row ${i + 2}: Question cannot exceed ${questionMaxLength} characters` };
                                }
                                if (row[1].length === 0) {
                                    return { error: `Row ${i + 2}: Answer is empty` };
                                }
                                if (row[1].length > answerMaxLength) {
                                    return { error: `Row ${i + 2}: Answer cannot exceed ${answerMaxLength} characters` };
                                }
                            }
                            return {};
                        }}
                        onFileChange={handleCsvFileChange}
                        onSubmit={() => {}}
                        instructionLines={[
                            'Uploading a CSV file containing your FAQs will replace all the FAQs you have entered currently',
                            `Enter at most ${maxItems} FAQs`,
                        ]}
                        showPreview={false}
                    />
                </MuiCore.DialogContent>
                <MuiCore.DialogActions style={{ padding: '16px 24px', borderTop: '1px solid #e0e0e0' }}>
                    <MuiCore.Button
                        onClick={handleCsvCancel}
                        variant="outlined"
                        style={{ fontFamily: 'Sofia', textTransform: 'none', borderRadius: 100 }}
                    >
                        Cancel
                    </MuiCore.Button>
                    <MuiCore.Button
                        onClick={handleCsvConfirm}
                        variant="contained"
                        disabled={csvSubmitDisabled}
                        style={{
                            backgroundColor: csvSubmitDisabled ? undefined : '#7F2062',
                            color: csvSubmitDisabled ? undefined : '#fff',
                            fontFamily: 'Sofia',
                            textTransform: 'none',
                            borderRadius: 100,
                        }}
                    >
                        Confirm
                    </MuiCore.Button>
                </MuiCore.DialogActions>
            </MuiCore.Dialog>
        </MuiCore.Box>
    );
};

export default FaqCard;
